import { APICONFIG } from '@config/api.config';
import { APIService } from '@services/api/common-services';
import { useQuery } from '@tanstack/react-query';
import { IQueryResponse } from '@services/api';
import { ErrorHandler } from '@helper/errorHandler';
import { useCheckRxdb } from '@hooks/checkRxdb';
import { setRxdb } from '@helper/utils';
import { ISupervisorDetail, ISupervisorDetailRBDM } from '@components/supervisorList';
import { useSupervisorCollection } from './useSupervisorCollection';
import { usePermissionCheckForApiCallback } from '../../../authGuard';

export const useSupervisorList = (
	forceFetch = false
): IQueryResponse<Array<ISupervisorDetail | ISupervisorDetailRBDM>> => {
	const { isProfileLoaded, isRBDM, userRole } = useSupervisorCollection();

	// Use the SAME collection for both regular and RBDM users (like the working end user implementation)
	const { isDbChecked, shouldFetch } = useCheckRxdb('supervisor_list');
	const { permissionForApi } = usePermissionCheckForApiCallback({ componentName: 'Supervisor' });

	const fetchSupervisorListing = async (): Promise<Array<ISupervisorDetail | ISupervisorDetailRBDM>> => {
		try {
			// Choose API endpoint based on user role (like the working end user implementation)
			const apiEndpoint = isRBDM ? APICONFIG.SUPERVISOR_LIST_RBDM : APICONFIG.SUPERVISOR_LIST_BY_CONTACT;

			const response = await APIService.getData<Array<ISupervisorDetail | ISupervisorDetailRBDM>>(apiEndpoint, {
				limit: '0',
			});
			if (response) {
				const data = response.map((item) => {
					return {
						...item,
						id: item?.id?.toString(),
						fullName: `${item?.firstname} ${item.lastname}`,
					};
				});
				// Store in the SAME collection for both user types
				await setRxdb('supervisor_list', data);
			}
			return response;
		} catch (error) {
			ErrorHandler(error);
			return [];
		}
	};

	const { data, error, isLoading, isPending, isFetching, refetch } = useQuery({
		queryKey: ['supervisorListing', userRole], // Include role in query key like the working implementation
		queryFn: fetchSupervisorListing,
		// Only enable query when profile is loaded and database is checked (like the working implementation)
		enabled: permissionForApi ? (isProfileLoaded && isDbChecked && shouldFetch) || forceFetch : false,
		refetchOnWindowFocus: false,
		retry: false,
	});

	return {
		data,
		error,
		isLoading,
		isPending,
		isFetching,
		refetch,
	};
};
